import { Check, Eye } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  buildClaimsFilingUrl,
  resolvePremisesLorStep,
} from '@/components/claims-filing/buildClaimsFilingUrl';
import { ClaimFilingStep } from '@/components/claims-filing/hooks/useClaimFilingState';
import {
  buildPremisesFollowUpUrl,
  getCurrentPremisesFollowUpTask,
  getLatestPremisesSendLorTask,
  getPremisesFollowUpLabel,
  getPremisesFollowUpStepKey,
  isTaskNotApplicable,
} from '@/components/claims-filing/premises/premisesFlow';
import { useCoverageStepProgress } from '@/components/claims-filing/state/useCoverageStepProgress';
import { TaskDetail } from '@/components/tasks/task-detail';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogTitle,
  RightDialogContent,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  useCreateTaskMutation,
  useGetTaskGroupsQuery,
  useGetTasksQuery,
  useGetTaskStatusesQuery,
  useGetTaskTypesQuery,
  useUpdateTaskMutation,
} from '@/services/api';
import {
  Task,
  TaskGroup,
  TaskType,
} from '@/services/types/client-intake-types';
import {
  CoverageCategory,
  CoverageSchema,
  CoverageStatus,
} from '@/services/types/coverage-types';
import {
  CanonicalTaskGroup,
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

type SimpleStepKey =
  | 'limits'
  | 'review'
  | 'call'
  | 'lor'
  | 'review_send'
  | 'ack'
  // Premises-specific steps
  | 'confirm_location'
  | 'research_property'
  | 'send_lor_premises'
  | 'open_premises_claim'
  | 'research_alternate_contacts';

interface CoverageStepRowProps {
  matterId: string;
  coverage: CoverageSchema;
  isHighlighted?: boolean;
}

export const CoverageStepRow = ({
  matterId,
  coverage,
  isHighlighted,
}: CoverageStepRowProps) => {
  const navigate = useNavigate();
  const [selectedTaskId, setSelectedTaskId] = React.useState<string | null>(
    null,
  );
  const {
    reviewDone,
    reviewCompletedAt,
    callDone,
    lorDone: lorDoneFromHook,
    reviewSendDone: reviewSendDoneFromHook,
    ackDone,
    callCompletedAt,
    lorCompletedAt,
    ackCompletedAt,
    sendLorPremisesDone: sendLorPremisesDoneFromHook,
    sendLorPremisesNA: sendLorPremisesNAFromHook,
  } = useCoverageStepProgress(matterId, coverage.id);
  const progressId = `progress-${coverage.id}`;
  const isNotApplicable = coverage.status === CoverageStatus.NOT_APPLICABLE;
  const handleNotApplicableClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toast.info('This coverage is marked Not Applicable and cannot be actioned');
  };

  const buildLink = (step: ClaimFilingStep) =>
    buildClaimsFilingUrl(matterId, coverage.id, step);
  const buildPremisesLorLink = () =>
    buildLink(
      resolvePremisesLorStep({
        lorDone: sendLorPremisesDoneOrNA,
        reviewSendDone: reviewSendDoneOrNA,
      }),
    );
  const buildLimitsLink = () =>
    `/matters/${matterId}/coverage/${coverage.id}/edit?tab=benefits`;

  const formatDate = (iso?: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const coverageLimitsHref = buildLimitsLink();

  // Task mutation wiring for inline acknowledgement completion
  const { data: tasksResponse } = useGetTasksQuery({
    matter: matterId,
    limit: 200,
  });
  const { data: taskStatuses = [] } = useGetTaskStatusesQuery();
  const [updateTask, { isLoading: isUpdatingAck }] = useUpdateTaskMutation();
  const { data: taskTypes = [] } = useGetTaskTypesQuery();
  const { data: taskGroups = [] } = useGetTaskGroupsQuery();
  const [createTask, { isLoading: isCreatingOpenFile }] =
    useCreateTaskMutation();
  const [isTogglingReview, setIsTogglingReview] = React.useState(false);

  // Tasks scoped to this coverage
  const coverageTasks: Task[] = React.useMemo(() => {
    const tasks = (tasksResponse?.results as Task[]) || [];
    return tasks.filter(
      (t) =>
        t.coverage_id === coverage.id ||
        t.coverage?.id === coverage.id,
    );
  }, [tasksResponse?.results, coverage.id]);
  // All tasks on the matter (for global 1P/3P "Find Coverage" tasks)
  const matterTasks: Task[] = React.useMemo(() => {
    return ((tasksResponse?.results as Task[]) || []) as Task[];
  }, [tasksResponse?.results]);

  // Determine if this is a premises coverage early (needed for task lookup)
  const isPremisesCategory = coverage.category === CoverageCategory.PROPERTY;

  const getTaskByType = React.useCallback(
    (type: CanonicalTaskType) =>
      coverageTasks.find((t) => t.type?.canonical_type === type),
    [coverageTasks],
  );

  const isTaskDone = (task?: Task) => {
    const status: CanonicalTaskStatus | null =
      task?.status?.canonical_status ?? null;
    return status === CanonicalTaskStatus.COMPLETED;
  };

  const isTaskNA = (task?: Task) =>
    (task?.status?.canonical_status ?? null) ===
    CanonicalTaskStatus.NOT_APPLICABLE;

  // Health coverage: locate the Open File task for this coverage
  const openFileTask = React.useMemo(() => {
    return getTaskByType(CanonicalTaskType.OPEN_FILE);
  }, [getTaskByType]);
  const openFileDone =
    (openFileTask?.status?.canonical_status ?? null) ===
    CanonicalTaskStatus.COMPLETED;
  const openFileNA =
    (openFileTask?.status?.canonical_status ?? null) ===
    CanonicalTaskStatus.NOT_APPLICABLE;
  const openFileNAAt: string | null = openFileNA
    ? (openFileTask?.modified_at ?? null)
    : null;

  // Map steps to their specific tasks for THIS coverage
  const reviewTask = React.useMemo(
    () => getTaskByType(CanonicalTaskType.REVIEW_FOUND_COVERAGE),
    [getTaskByType],
  );
  // Global "Find Coverage" tasks (matter-level)
  const find1pTasks = React.useMemo(
    () =>
      matterTasks.filter(
        (t) => t.type?.canonical_type === CanonicalTaskType.FIND_1P_COVERAGE,
      ),
    [matterTasks],
  );
  const find3pTasks = React.useMemo(
    () =>
      matterTasks.filter(
        (t) => t.type?.canonical_type === CanonicalTaskType.FIND_3P_COVERAGE,
      ),
    [matterTasks],
  );
  const callTask = React.useMemo(
    () => getTaskByType(CanonicalTaskType.OPEN_CLAIM),
    [getTaskByType],
  );
  const lorTask = React.useMemo(
    () => getTaskByType(CanonicalTaskType.SEND_LOR),
    [getTaskByType],
  );
  const ackTask = React.useMemo(
    () => getTaskByType(CanonicalTaskType.LOR_ACKNOWLEDGEMENT),
    [getTaskByType],
  );

  // Premises-specific tasks
  // Review properties is matter-level; downstream tasks are coverage-specific.
  const confirmLocationTask = React.useMemo(
    () =>
      matterTasks.find(
        (t) =>
          t.type?.canonical_type ===
          CanonicalTaskType.REVIEW_INCIDENT_PROPERTIES,
      ),
    [matterTasks],
  );
  const researchPropertyTask = React.useMemo(
    () =>
      coverageTasks.find(
        (t) =>
          t.type?.canonical_type === CanonicalTaskType.RESEARCH_PROPERTY_OWNERS,
      ),
    [coverageTasks],
  );
  // For premises, the LOR task uses the regular SEND_LOR type linked to the coverage
  const sendLorPremisesTask = React.useMemo(
    () =>
      isPremisesCategory
        ? getLatestPremisesSendLorTask(coverageTasks)
        : undefined,
    [isPremisesCategory, coverageTasks],
  );
  const premisesFollowUpTask = React.useMemo(
    () =>
      isPremisesCategory ? getCurrentPremisesFollowUpTask(coverageTasks) : undefined,
    [isPremisesCategory, coverageTasks],
  );
  const premisesFollowUpKey = getPremisesFollowUpStepKey(premisesFollowUpTask);
  const premisesFollowUpLabel = getPremisesFollowUpLabel(premisesFollowUpTask);
  const premisesFollowUpHref = buildPremisesFollowUpUrl(
    matterId,
    coverage.id,
    premisesFollowUpTask,
  );

  // 3P uses "Get Liability Limits" task; 1P uses "Declaration Page" task
  const limitsTask3P = React.useMemo(
    () => getTaskByType(CanonicalTaskType.GET_LIABILITY_LIMITS),
    [getTaskByType],
  );
  const decPageTask1P = React.useMemo(
    () => getTaskByType(CanonicalTaskType.DECLARATION_PAGE),
    [getTaskByType],
  );
  // Select the appropriate task based on coverage category
  const limitsTask =
    coverage.category === CoverageCategory.FIRST_PARTY
      ? decPageTask1P
      : limitsTask3P;
  const hasLimitsTask = Boolean(limitsTask);
  const limitsDone = isTaskDone(limitsTask);
  const limitsNA = isTaskNA(limitsTask);
  const limitsDoneAt = limitsTask?.completed_at ?? null;
  const limitsNAAt: string | null = limitsNA
    ? (limitsTask?.modified_at ?? null)
    : null;
  const limitsDoneOrNA = limitsDone || limitsNA;
  const limitsCompleteForFlow = hasLimitsTask ? limitsDoneOrNA : true;

  // Derive completion for "Review Information":
  // Prefer matter-level "Find 1P/3P Coverage" tasks based on coverage category; fallback to coverage-specific REVIEW_FOUND_COVERAGE
  const {
    reviewIsDone,
    reviewDoneAtResolved,
    reviewIsNA,
    reviewNAAt,
    reviewNALabel,
  }: {
    reviewIsDone: boolean;
    reviewDoneAtResolved: string | null;
    reviewIsNA: boolean;
    reviewNAAt: string | null;
    reviewNALabel?: string | null;
  } = React.useMemo(() => {
    let done = false;
    let isNA = false;
    let doneAt: string | null = null;
    let naAt: string | null = null;
    let naLabel: string | null = null;
    if (coverage.category === CoverageCategory.FIRST_PARTY) {
      const anyCompleted = find1pTasks.some(
        (t) => t.status?.canonical_status === CanonicalTaskStatus.COMPLETED,
      );
      const anyNA = find1pTasks.some(
        (t) =>
          t.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE,
      );
      if (anyCompleted || anyNA) {
        done = true;
        isNA = anyNA && !anyCompleted;
        if (anyNA) {
          const naCandidates = find1pTasks.filter(
            (t) =>
              t.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE,
          );
          const latestNA = naCandidates
            .map((t) => t.modified_at)
            .filter(Boolean)
            .sort()
            .at(-1);
          naAt = (latestNA as string) ?? null;
          // Prefer a descriptive NA label when available
          const hasNoCoverageLabel = naCandidates.some(
            (t) =>
              (t.status?.name || '').toLowerCase() ===
              'no coverage found'.toLowerCase(),
          );
          if (hasNoCoverageLabel) {
            naLabel = 'No coverage found';
          }
        }
        const latest = find1pTasks
          .filter(
            (t) => t.status?.canonical_status === CanonicalTaskStatus.COMPLETED,
          )
          .map((t) => t.completed_at)
          .filter(Boolean)
          .sort()
          .at(-1);
        doneAt = (latest as string) ?? null;
      }
    } else if (coverage.category === CoverageCategory.THIRD_PARTY) {
      const anyCompleted = find3pTasks.some(
        (t) => t.status?.canonical_status === CanonicalTaskStatus.COMPLETED,
      );
      const anyNA = find3pTasks.some(
        (t) =>
          t.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE,
      );
      if (anyCompleted || anyNA) {
        done = true;
        isNA = anyNA && !anyCompleted;
        if (anyNA) {
          const naCandidates = find3pTasks.filter(
            (t) =>
              t.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE,
          );
          const latestNA = naCandidates
            .map((t) => t.modified_at)
            .filter(Boolean)
            .sort()
            .at(-1);
          naAt = (latestNA as string) ?? null;
          const hasNoCoverageLabel = naCandidates.some(
            (t) =>
              (t.status?.name || '').toLowerCase() ===
              'no coverage found'.toLowerCase(),
          );
          if (hasNoCoverageLabel) {
            naLabel = 'No coverage found';
          }
        }
        const latest = find3pTasks
          .filter(
            (t) => t.status?.canonical_status === CanonicalTaskStatus.COMPLETED,
          )
          .map((t) => t.completed_at)
          .filter(Boolean)
          .sort()
          .at(-1);
        doneAt = (latest as string) ?? null;
      }
    }
    // Fallback to coverage-specific "Review Found Coverage"
    if (!done) {
      const completed = isTaskDone(reviewTask) || reviewDone;
      const na = isTaskNA(reviewTask);
      done = completed || na;
      isNA = !completed && na;
      doneAt = reviewTask?.completed_at ?? reviewCompletedAt ?? null;
      naAt = na ? (reviewTask?.modified_at ?? null) : null;
    }
    return {
      reviewIsDone: done,
      reviewDoneAtResolved: doneAt,
      reviewIsNA: isNA,
      reviewNAAt: naAt,
      reviewNALabel: naLabel,
    };
  }, [
    coverage.category,
    find1pTasks,
    find3pTasks,
    reviewTask,
    reviewDone,
    reviewCompletedAt,
  ]);
  const fileDone = isTaskDone(callTask) || callDone;
  const fileNA = isTaskNA(callTask);
  const fileNAAt: string | null = fileNA
    ? (callTask?.modified_at ?? null)
    : null;
  const lorDone = isTaskDone(lorTask) || lorDoneFromHook;
  // reviewSendDone is true ONLY when the task is fully completed (not just under review)
  const reviewSendDone = isTaskDone(lorTask) || reviewSendDoneFromHook;
  const lorNA = isTaskNA(lorTask);
  const lorNAAt: string | null = lorNA ? (lorTask?.modified_at ?? null) : null;
  const ackDoneStrict =
    (ackTask?.status?.canonical_status ?? null) ===
      CanonicalTaskStatus.COMPLETED || ackDone;
  const ackNA = isTaskNA(ackTask);
  const ackNAAt: string | null = ackNA ? (ackTask?.modified_at ?? null) : null;

  // Treat NA as done for UI display and progression
  const fileDoneOrNA = fileDone || fileNA;
  const lorDoneOrNA = lorDone || lorNA;
  const reviewSendDoneOrNA = reviewSendDone || lorNA;
  const ackDoneOrNA = ackDoneStrict || ackNA;

  // Premises-specific step completion (isPremisesCategory defined above)
  const confirmLocationDone = isTaskDone(confirmLocationTask);
  const confirmLocationNA = isTaskNA(confirmLocationTask);
  const confirmLocationDoneOrNA = confirmLocationDone || confirmLocationNA;
  const researchPropertyDone = isTaskDone(researchPropertyTask);
  const researchPropertyNA = isTaskNA(researchPropertyTask);
  const researchPropertyDoneOrNA = researchPropertyDone || researchPropertyNA;
  const sendLorPremisesDone = sendLorPremisesDoneFromHook;
  const sendLorPremisesNA = sendLorPremisesNAFromHook;
  const sendLorPremisesDoneOrNA = sendLorPremisesDone || sendLorPremisesNA;
  const premisesFollowUpDone = isTaskDone(premisesFollowUpTask);
  const premisesFollowUpNA = isTaskNotApplicable(premisesFollowUpTask);
  const premisesFollowUpDoneOrNA = premisesFollowUpDone || premisesFollowUpNA;

  let nextKey: SimpleStepKey | null = null;
  if (isPremisesCategory) {
    // Premises workflow: Review Incident Properties
    // -> Research Property & Owners -> Write a Letter of Representation
    if (!confirmLocationDoneOrNA) nextKey = 'confirm_location';
    else if (!researchPropertyDoneOrNA) nextKey = 'research_property';
    else if (!sendLorPremisesDoneOrNA) nextKey = 'send_lor_premises';
    else if (
      premisesFollowUpTask &&
      premisesFollowUpKey &&
      !premisesFollowUpDoneOrNA
    ) {
      nextKey = premisesFollowUpKey;
    }
  } else if (!reviewIsDone) {
    // Standard workflow
    nextKey = 'review';
  } else if (!fileDoneOrNA) {
    nextKey = 'call';
  } else if (!lorDoneOrNA) {
    nextKey = 'lor';
  } else if (!reviewSendDoneOrNA) {
    nextKey = 'review_send';
  } else if (!ackDoneOrNA) {
    nextKey = 'ack';
  } else if (!limitsCompleteForFlow) {
    nextKey = 'limits';
  }

  const handleMarkAckReceived = async () => {
    if (!ackTask) return;
    const completedStatus = taskStatuses.find(
      (s) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
    );
    if (!completedStatus) return;
    try {
      await updateTask({
        matterId,
        taskId: ackTask.id,
        task: {
          status_id: completedStatus.id,
          completed_at: new Date().toISOString(),
        },
      }).unwrap();
    } catch {
      // noop
    }
  };

  const handleToggleReviewComplete = async () => {
    // Determine target tasks to toggle
    let targets: Task[] = [];
    if (
      coverage.category === CoverageCategory.FIRST_PARTY &&
      find1pTasks.length > 0
    ) {
      targets = find1pTasks;
    } else if (
      coverage.category === CoverageCategory.THIRD_PARTY &&
      find3pTasks.length > 0
    ) {
      targets = find3pTasks;
    } else if (reviewTask) {
      targets = [reviewTask];
    } else {
      return;
    }
    const completedStatus = taskStatuses.find(
      (s) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
    );
    const pendingStatus = taskStatuses.find(
      (s) => s.canonical_status === CanonicalTaskStatus.PENDING,
    );
    if (!completedStatus || !pendingStatus) return;
    // Toggle rule: if any target is not completed, set all to completed; otherwise set all to pending
    const anyNotCompleted = targets.some(
      (t) => t.status?.canonical_status !== CanonicalTaskStatus.COMPLETED,
    );
    const nextStatusId = anyNotCompleted
      ? completedStatus.id
      : pendingStatus.id;
    const nextCompletedAt =
      nextStatusId === completedStatus.id ? new Date().toISOString() : null;
    try {
      setIsTogglingReview(true);
      await Promise.all(
        targets.map((t) =>
          updateTask({
            matterId,
            taskId: t.id,
            task: {
              status_id: nextStatusId,
              completed_at: nextCompletedAt,
            },
          }).unwrap(),
        ),
      );
    } catch {
      // noop
    } finally {
      setIsTogglingReview(false);
    }
  };

  // Stabilize optional label for linter/type parsing
  const reviewNaLabelLocal = reviewNALabel ?? null;

  // Premises-specific rows for PROPERTY category
  const premisesRows: Array<{
    key: SimpleStepKey;
    label: string;
    done: boolean;
    na: boolean;
    naAt?: string | null;
    naLabel?: string | null;
    doneText: string;
    doneAt?: string | null;
    task?: Task;
    href?: string;
  }> = [
    {
      key: 'confirm_location',
      label: 'Review Incident Properties',
      done: confirmLocationDoneOrNA,
      na: confirmLocationNA,
      naAt: confirmLocationNA
        ? (confirmLocationTask?.modified_at ?? null)
        : null,
      doneText: 'Reviewed',
      doneAt: confirmLocationTask?.completed_at ?? null,
      task: confirmLocationTask,
      href: `/matters/${matterId}/premises/${coverage.id}/confirm-location`,
    },
    {
      key: 'research_property',
      label: 'Research Property & Owners',
      done: researchPropertyDoneOrNA,
      na: researchPropertyNA,
      naAt: researchPropertyNA
        ? (researchPropertyTask?.modified_at ?? null)
        : null,
      doneText: 'Researched',
      doneAt: researchPropertyTask?.completed_at ?? null,
      task: researchPropertyTask,
      href: `/matters/${matterId}/premises/${coverage.id}/research-property`,
    },
    {
      key: 'send_lor_premises',
      label: 'Write a Letter of Representation',
      done: sendLorPremisesDoneOrNA,
      na: sendLorPremisesNA,
      naAt: sendLorPremisesNA
        ? (sendLorPremisesTask?.modified_at ?? null)
        : null,
      doneText: 'Drafted',
      doneAt: sendLorPremisesTask?.completed_at ?? null,
      task: sendLorPremisesTask,
      href: buildPremisesLorLink(),
    },
  ];

  if (premisesFollowUpTask && premisesFollowUpKey && premisesFollowUpLabel) {
    premisesRows.push({
      key: premisesFollowUpKey,
      label: premisesFollowUpLabel,
      done: premisesFollowUpDoneOrNA,
      na: premisesFollowUpNA,
      naAt: premisesFollowUpNA
        ? (premisesFollowUpTask?.modified_at ?? null)
        : null,
      doneText:
        premisesFollowUpTask.type?.canonical_type ===
        CanonicalTaskType.OPEN_PREMISES_CLAIM
          ? 'Opened'
          : 'Researched',
      doneAt: premisesFollowUpTask?.completed_at ?? null,
      task: premisesFollowUpTask,
      href: premisesFollowUpHref ?? undefined,
    });
  }

  const rows: Array<{
    key: SimpleStepKey;
    label: string;
    done: boolean;
    na: boolean;
    naAt?: string | null;
    naLabel?: string | null;
    doneText: string;
    doneAt?: string | null;
    ctaLabel?: string;
    ctaHref?: string;
    reviewHref?: string;
    ctaOnClick?: () => void;
  }> = [
    {
      key: 'review',
      label: 'Review Information',
      done: reviewIsDone,
      na: reviewIsNA,
      naAt: reviewNAAt,
      naLabel: reviewNaLabelLocal,
      doneText: 'Reviewed',
      doneAt: reviewDoneAtResolved,
      ctaLabel: 'Mark as complete',
      ctaOnClick: handleToggleReviewComplete,
      reviewHref: buildLink('review'),
    },
    {
      key: 'call',
      label: 'Open Claim',
      done: fileDoneOrNA,
      na: fileNA,
      naAt: fileNAAt,
      doneText: 'Filed',
      doneAt: callTask?.completed_at ?? callCompletedAt ?? null,
      ctaLabel: 'Start Filing',
      ctaHref: buildLink('call'),
      reviewHref: buildLink('call'),
    },
    {
      key: 'lor',
      label: 'Write a Letter of Representation',
      done: lorDoneOrNA,
      na: lorNA,
      naAt: lorNAAt,
      doneText: 'Drafted',
      doneAt: lorTask?.completed_at ?? lorCompletedAt ?? null,
      ctaLabel: 'Draft Letter',
      ctaHref: buildLink('lor'),
      reviewHref: buildLink('lor'),
    },
    {
      key: 'review_send',
      label: 'Review & Send LOR',
      done: reviewSendDoneOrNA,
      na: lorNA,
      naAt: lorNAAt,
      doneText: 'Sent',
      doneAt: lorTask?.completed_at ?? lorCompletedAt ?? null,
      ctaLabel: 'Review & Send',
      ctaHref: buildLink('review_send'),
      reviewHref: buildLink('review_send'),
    },
    {
      key: 'ack',
      label: 'Acknowledgement',
      done: ackDoneOrNA,
      na: ackNA,
      naAt: ackNAAt,
      doneText: 'Acknowledged',
      doneAt: ackTask?.completed_at ?? ackCompletedAt ?? null,
      ctaLabel: 'Mark as received',
      ctaOnClick: handleMarkAckReceived,
      reviewHref: buildLink('ack'),
    },
    {
      key: 'limits',
      label:
        coverage.category === CoverageCategory.FIRST_PARTY
          ? 'Review and Enter 1P Benefits'
          : 'Find Limits',
      done: limitsDone,
      na: limitsNA,
      naAt: limitsNAAt,
      doneText: 'Recorded',
      doneAt: limitsDoneAt,
      ctaLabel: 'Update limits',
      ctaHref: coverageLimitsHref,
      reviewHref: coverageLimitsHref,
    },
  ];

  return (
    <div
      className={cn(
        'bg-white border-l border-zinc-100 h-full',
        isHighlighted && 'ring-2 ring-violet-200',
        isNotApplicable && 'opacity-60',
      )}
      data-coverage-step-row={coverage.id}
      aria-label="Claims filing progress"
      role="region"
      aria-labelledby={progressId}
    >
      <div className={cn('p-2', 'h-full')}>
        <div className="space-y-0.5" role="list" aria-label="Steps">
          {/* Premises workflow for PROPERTY category */}
          {isPremisesCategory ? (
            premisesRows.map((row) => {
              const isNext = nextKey === row.key;
              const isDone = row.done;
              const dateStr = formatDate(row.doneAt);
              let labelTone = 'text-zinc-400';
              if (isNext) labelTone = 'text-zinc-900 font-medium';
              if (isDone) labelTone = 'text-emerald-700';
              const labelClasses = cn(
                'text-[12px]',
                labelTone,
                !isDone &&
                  isNotApplicable &&
                  'text-zinc-400 cursor-not-allowed hover:no-underline',
              );

              return (
                <div
                  key={row.key}
                  className={cn(
                    'px-1.5 py-1 flex items-center justify-between gap-1.5 rounded',
                  )}
                >
                  <div className="flex items-center gap-1.5">
                    {isDone ? (
                      <Check className="h-3 w-3 text-emerald-600" aria-hidden />
                    ) : (
                      <span className="inline-block h-3 w-3" aria-hidden />
                    )}
                    <button
                      type="button"
                      className={cn(
                        labelClasses,
                        'hover:underline cursor-pointer',
                      )}
                      aria-label={
                        row.href
                          ? `Go to ${row.label}`
                          : `View ${row.label} task`
                      }
                      onClick={(e) => {
                        if (isNotApplicable) {
                          handleNotApplicableClick(e);
                          return;
                        }
                        e.stopPropagation();
                        // If row has an href, navigate to it
                        if (row.href) {
                          navigate(row.href);
                          return;
                        }
                        // Otherwise, open task detail
                        if (row.task?.id) {
                          setSelectedTaskId(row.task.id);
                        }
                      }}
                      disabled={isNotApplicable || (!row.href && !row.task?.id)}
                    >
                      {row.label}
                      {row.na && (
                        <span className="ml-1.5 inline-flex items-center rounded px-1 py-0.5 text-[9px] font-medium bg-zinc-100 text-zinc-600 border border-zinc-200">
                          NA
                        </span>
                      )}
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {row.task?.id && (
                      <button
                        type="button"
                        aria-label={`View ${row.label} task`}
                        className={cn(
                          'h-5 w-5 flex items-center justify-center rounded text-zinc-300 hover:text-zinc-600 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400',
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTaskId(row.task!.id!);
                        }}
                      >
                        <Eye className="h-3 w-3" aria-hidden />
                      </button>
                    )}
                    {isDone &&
                      (row.na ? (
                        <div className="text-[10px] font-medium text-emerald-700 whitespace-nowrap">
                          {`NA${row.naAt ? ` ${formatDate(row.naAt)}` : ''}`}
                        </div>
                      ) : (
                        dateStr && (
                          <div className="text-[10px] font-medium text-zinc-500 whitespace-nowrap">
                            {`${row.doneText} ${dateStr}`}
                          </div>
                        )
                      ))}
                  </div>
                </div>
              );
            })
          ) : coverage.category === CoverageCategory.FIRST_PARTY_HEALTH ? (
            <div
              className={cn(
                'px-2 py-1.5 flex items-center justify-between gap-2 rounded-lg',
              )}
            >
              <div className="flex items-center gap-2">
                {openFileDone || openFileNA ? (
                  <Check className="h-3 w-3 text-green-700" aria-hidden />
                ) : (
                  <span className="inline-block h-3 w-3" aria-hidden />
                )}
                <button
                  type="button"
                  className={cn(
                    'text-[14px] hover:underline cursor-pointer',
                    openFileDone || openFileNA
                      ? 'text-green-700'
                      : 'text-gray-900 font-medium',
                    isNotApplicable &&
                      'text-gray-400 cursor-not-allowed hover:no-underline',
                  )}
                  aria-label="Open File task"
                  onClick={(e) => {
                    if (isNotApplicable) {
                      handleNotApplicableClick(e);
                      return;
                    }
                    e.stopPropagation();
                    if (openFileTask?.id) {
                      setSelectedTaskId(openFileTask.id);
                      return;
                    }
                    try {
                      const openFileType = (
                        (taskTypes as TaskType[]) || []
                      ).find(
                        (t: TaskType) =>
                          t?.canonical_type === CanonicalTaskType.OPEN_FILE,
                      );
                      const liensGroup = (
                        (taskGroups as TaskGroup[]) || []
                      ).find(
                        (g: TaskGroup) =>
                          g?.canonical_group === CanonicalTaskGroup.LIENS,
                      );
                      if (!openFileType || !liensGroup) {
                        toast.error(
                          'Missing task metadata to create Open File task',
                        );
                        return;
                      }
                      (async () => {
                        try {
                          const created = await createTask({
                            matterId,
                            task: {
                              name: 'Open File',
                              type_id: openFileType.id,
                              group_id: liensGroup.id,
                              coverage_id: coverage.id,
                            },
                          }).unwrap();
                          if (created?.id) {
                            setSelectedTaskId(created.id);
                          }
                        } catch {
                          toast.error('Failed to create Open File task');
                        }
                      })();
                    } catch {
                      // noop
                    }
                  }}
                  disabled={isNotApplicable || isCreatingOpenFile}
                >
                  Open File
                  {openFileNA && (
                    <span className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-700 border border-gray-200">
                      NA
                    </span>
                  )}
                </button>
              </div>
              <div className="flex items-center gap-2">
                {openFileTask?.id && (
                  <button
                    type="button"
                    aria-label="View task"
                    className={cn(
                      'h-8 w-8 flex items-center justify-center rounded-md text-gray-300 hover:text-gray-600 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTaskId(openFileTask.id!);
                    }}
                  >
                    <Eye className="h-4 w-4" aria-hidden />
                  </button>
                )}
                {openFileDone && openFileTask?.completed_at && !openFileNA && (
                  <div className="text-xs text-gray-600 whitespace-nowrap">
                    {`Completed ${formatDate(openFileTask.completed_at)}`}
                  </div>
                )}
                {openFileNA && (
                  <div className="text-xs text-green-700 whitespace-nowrap">
                    Marked NA
                  </div>
                )}
              </div>
            </div>
          ) : (
            rows.map((row) => {
              const isNext = nextKey === row.key;
              const isDone = row.done;
              const dateStr = formatDate(row.doneAt);
              let labelTone = 'text-zinc-400';
              if (isNext) labelTone = 'text-zinc-900 font-medium';
              if (isDone) labelTone = 'text-emerald-700';
              const labelClasses = cn(
                'text-[12px]',
                labelTone,
                !isDone &&
                  isNotApplicable &&
                  'text-zinc-400 cursor-not-allowed hover:no-underline',
              );

              // Determine the task to open for this row
              const rowTask: Task | undefined = (() => {
                if (row.key === 'limits') return limitsTask;
                if (row.key === 'review') {
                  if (coverage.category === CoverageCategory.FIRST_PARTY) {
                    const candidates = find1pTasks;
                    const completed = candidates.find(
                      (t) =>
                        t.status?.canonical_status ===
                        CanonicalTaskStatus.COMPLETED,
                    );
                    if (completed) return completed;
                    const actionable = candidates.find(
                      (t) =>
                        t.status?.canonical_status !==
                        CanonicalTaskStatus.NOT_APPLICABLE,
                    );
                    return actionable || candidates[0];
                  }
                  if (coverage.category === CoverageCategory.THIRD_PARTY) {
                    const candidates = find3pTasks;
                    const completed = candidates.find(
                      (t) =>
                        t.status?.canonical_status ===
                        CanonicalTaskStatus.COMPLETED,
                    );
                    if (completed) return completed;
                    const actionable = candidates.find(
                      (t) =>
                        t.status?.canonical_status !==
                        CanonicalTaskStatus.NOT_APPLICABLE,
                    );
                    return actionable || candidates[0];
                  }
                  return reviewTask;
                }
                if (row.key === 'call') return callTask;
                if (row.key === 'lor') return lorTask;
                if (row.key === 'ack') return ackTask;
                return undefined;
              })();
              return (
                <div
                  key={row.key}
                  className={cn(
                    'px-1.5 py-1 flex items-center justify-between gap-1.5 rounded',
                  )}
                >
                  <div className="flex items-center gap-1.5">
                    {isDone ? (
                      <Check className="h-3 w-3 text-emerald-600" aria-hidden />
                    ) : (
                      <span className="inline-block h-3 w-3" aria-hidden />
                    )}
                    {row.reviewHref ? (
                      <Link
                        to={row.reviewHref}
                        className={cn(
                          labelClasses,
                          'hover:underline cursor-pointer',
                        )}
                        aria-label={`Open ${row.label}`}
                        onClick={(e) => {
                          if (isNotApplicable) {
                            handleNotApplicableClick(e);
                            return;
                          }
                          e.stopPropagation();
                        }}
                        aria-disabled={isNotApplicable}
                      >
                        {row.label}
                        {/* NA flag */}
                        {row.na && (
                          <span className="ml-1.5 inline-flex items-center rounded px-1 py-0.5 text-[9px] font-medium bg-zinc-100 text-zinc-600 border border-zinc-200">
                            {row.naLabel ?? 'NA'}
                          </span>
                        )}
                      </Link>
                    ) : (
                      <span className={labelClasses}>
                        {row.label}
                        {row.na && (
                          <span className="ml-1.5 inline-flex items-center rounded px-1 py-0.5 text-[9px] font-medium bg-zinc-100 text-zinc-600 border border-zinc-200">
                            {row.naLabel ?? 'NA'}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {rowTask?.id && (
                      <button
                        type="button"
                        aria-label={`View ${row.label} task`}
                        className={cn(
                          'h-5 w-5 flex items-center justify-center rounded text-zinc-300 hover:text-zinc-600 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400',
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTaskId(rowTask.id!);
                        }}
                      >
                        <Eye className="h-3 w-3" aria-hidden />
                      </button>
                    )}
                    {isDone &&
                      (row.na ? (
                        <div className="text-[10px] font-medium text-emerald-700 whitespace-nowrap">
                          {`NA${row.naAt ? ` ${formatDate(row.naAt)}` : ''}`}
                        </div>
                      ) : (
                        dateStr && (
                          <div className="text-[10px] font-medium text-zinc-500 whitespace-nowrap">{`${row.doneText} ${dateStr}`}</div>
                        )
                      ))}
                    {(() => {
                      if (
                        !isDone &&
                        isNext &&
                        row.ctaLabel &&
                        (row.ctaHref || row.ctaOnClick)
                      ) {
                        if (row.ctaHref) {
                          return (
                            <Button
                              asChild
                              size="sm"
                              className="h-6 px-2 text-[10px] bg-white text-zinc-800 border border-zinc-200 shadow-sm hover:bg-zinc-50"
                            >
                              <Link
                                to={row.ctaHref}
                                aria-label={row.ctaLabel}
                                onClick={(e) => {
                                  if (isNotApplicable) {
                                    handleNotApplicableClick(e);
                                    return;
                                  }
                                  e.stopPropagation();
                                }}
                                aria-disabled={isNotApplicable}
                                className={cn(
                                  isNotApplicable &&
                                    'cursor-not-allowed opacity-60',
                                )}
                              >
                                {row.ctaLabel}
                              </Link>
                            </Button>
                          );
                        }
                        return (
                          <Button
                            size="sm"
                            className="h-6 px-2 text-[10px] bg-white text-zinc-800 border border-zinc-200 shadow-sm hover:bg-zinc-50"
                            onClick={(e) => {
                              const disable =
                                (row.key === 'ack' &&
                                  (isUpdatingAck || !ackTask)) ||
                                (row.key === 'review' && isTogglingReview) ||
                                isNotApplicable;
                              if (disable) {
                                handleNotApplicableClick(e);
                                return;
                              }
                              e.stopPropagation();
                              row.ctaOnClick?.();
                            }}
                            disabled={
                              (row.key === 'ack' &&
                                (isUpdatingAck || !ackTask)) ||
                              (row.key === 'review' && isTogglingReview) ||
                              isNotApplicable
                            }
                          >
                            {(() => {
                              if (row.key === 'ack') {
                                return isUpdatingAck
                                  ? 'Marking…'
                                  : row.ctaLabel;
                              }
                              if (row.key === 'review' && isTogglingReview) {
                                return 'Updating…';
                              }
                              return row.ctaLabel;
                            })()}
                          </Button>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      {selectedTaskId && (
        <Dialog open onOpenChange={() => setSelectedTaskId(null)} modal={false}>
          <RightDialogContent className="bg-white h-[95vh] overflow-hidden flex flex-col">
            <DialogTitle className="sr-only">Task Details</DialogTitle>
            <TaskDetail
              taskId={selectedTaskId}
              matterId={matterId}
              onClose={() => setSelectedTaskId(null)}
              isModal={false}
            />
          </RightDialogContent>
        </Dialog>
      )}
    </div>
  );
};

/**
 * Hook to check if LOA Chaser should be shown for a coverage
 * Returns true when review, call, and LOR steps are complete
 */
export const useCoverageStepsCompletion = (
  matterId: string,
  coverage: CoverageSchema,
): { canShowLOAChaser: boolean; lorTaskId: string | null } => {
  const {
    reviewDone,
    callDone,
    lorDone: lorDoneFromHook,
    reviewSendDone: reviewSendDoneFromHook,
  } = useCoverageStepProgress(matterId, coverage.id);

  const { data: tasksResponse } = useGetTasksQuery({
    matter: matterId,
    limit: 200,
  });

  const coverageTasks: Task[] = React.useMemo(() => {
    const tasks = (tasksResponse?.results as Task[]) || [];
    return tasks.filter(
      (t) =>
        (t as unknown as { coverage_id?: string }).coverage_id ===
          coverage.id || t.coverage?.id === coverage.id,
    );
  }, [tasksResponse?.results, coverage.id]);

  const matterTasks: Task[] = React.useMemo(() => {
    return ((tasksResponse?.results as Task[]) || []) as Task[];
  }, [tasksResponse?.results]);

  const getTaskByType = React.useCallback(
    (type: CanonicalTaskType) =>
      coverageTasks.find((t) => t.type?.canonical_type === type),
    [coverageTasks],
  );

  const isTaskDone = (task?: Task) => {
    const status: CanonicalTaskStatus | null =
      task?.status?.canonical_status ?? null;
    return status === CanonicalTaskStatus.COMPLETED;
  };

  const isTaskNA = (task?: Task) =>
    (task?.status?.canonical_status ?? null) ===
    CanonicalTaskStatus.NOT_APPLICABLE;

  const find1pTasks = React.useMemo(
    () =>
      matterTasks.filter(
        (t) => t.type?.canonical_type === CanonicalTaskType.FIND_1P_COVERAGE,
      ),
    [matterTasks],
  );
  const find3pTasks = React.useMemo(
    () =>
      matterTasks.filter(
        (t) => t.type?.canonical_type === CanonicalTaskType.FIND_3P_COVERAGE,
      ),
    [matterTasks],
  );
  const reviewTask = React.useMemo(
    () => getTaskByType(CanonicalTaskType.REVIEW_FOUND_COVERAGE),
    [getTaskByType],
  );
  const callTask = React.useMemo(
    () => getTaskByType(CanonicalTaskType.OPEN_CLAIM),
    [getTaskByType],
  );
  const lorTask = React.useMemo(
    () => getTaskByType(CanonicalTaskType.SEND_LOR),
    [getTaskByType],
  );
  const ackTask = React.useMemo(
    () => getTaskByType(CanonicalTaskType.LOR_ACKNOWLEDGEMENT),
    [getTaskByType],
  );

  // Review completion
  const reviewIsDone = React.useMemo(() => {
    if (coverage.category === CoverageCategory.FIRST_PARTY) {
      return find1pTasks.some(
        (t) =>
          t.status?.canonical_status === CanonicalTaskStatus.COMPLETED ||
          t.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE,
      );
    }
    if (coverage.category === CoverageCategory.THIRD_PARTY) {
      return find3pTasks.some(
        (t) =>
          t.status?.canonical_status === CanonicalTaskStatus.COMPLETED ||
          t.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE,
      );
    }
    return isTaskDone(reviewTask) || isTaskNA(reviewTask) || reviewDone;
  }, [coverage.category, find1pTasks, find3pTasks, reviewTask, reviewDone]);

  const fileDone = isTaskDone(callTask) || callDone;
  const fileNA = isTaskNA(callTask);
  const fileDoneOrNA = fileDone || fileNA;

  const lorDone = isTaskDone(lorTask) || lorDoneFromHook;
  const lorNA = isTaskNA(lorTask);
  const lorDoneOrNA = lorDone || lorNA;

  // Review & Send step - must be completed before showing LOA Chaser
  // reviewSendDone means the LOR task is fully completed (not just under review)
  const reviewSendDone = isTaskDone(lorTask) || reviewSendDoneFromHook;
  const reviewSendDoneOrNA = reviewSendDone || lorNA;

  // Acknowledgement status - only show LOA Chaser when in ack step (not completed)
  const ackDone = isTaskDone(ackTask);
  const ackNA = isTaskNA(ackTask);
  const ackDoneOrNA = ackDone || ackNA;

  const isNotApplicable = coverage.status === CoverageStatus.NOT_APPLICABLE;
  const isHealthCoverage =
    coverage.category === CoverageCategory.FIRST_PARTY_HEALTH;

  // LOA Chaser shows only when we're IN the acknowledgement step:
  // - All prior steps done (review, file/call, LOR, review_send)
  // - Acknowledgement NOT done yet
  const canShowLOAChaser =
    reviewIsDone &&
    fileDoneOrNA &&
    lorDoneOrNA &&
    reviewSendDoneOrNA &&
    !ackDoneOrNA &&
    !isHealthCoverage &&
    !isNotApplicable;

  return { canShowLOAChaser, lorTaskId: lorTask?.id ?? null };
};
